import { Checkbox, Col, Form, Row, Tooltip } from 'antd';
import styles from './DatastoreScalingNodeInfoCard.module.less';
import CcxIcon1NodeDeployment from '../../ccx/icons/CcxIcon1NodeDeployment';
import TypographyText from '../../../tmp/TypographyText';
import Space from '../../common/antd/Space';
import { CcxIconCpu, CcxIconDisk, CcxIconRam } from '../../ccx/icons/Icons';
import AppForm from '../../ccx/common/AppForm';
import { useEffect, useMemo, useState } from 'react';
import DeploymentsItem from '../../../types/DeploymentsItem';
import useContentInstanceSizes from '../../../core/hooks/useContentInstanceSizes';
import { InstanceSizeProps } from '../../../services/ContentService';
import InstanceSize from '../../../types/InstanceSize';
import { getHostnameOrIP } from '../../../core/helpers';
import AppFlagIcon from '../../ccx/common/AppFlagIcon';
import { FormInstance } from 'antd/es';
import InfoIcon from '@severalnines/bar-frontend-components/build/lib/General/InfoIcon';
import DeploymentOptions from '../../../types/DeploymentOptions';
import LazyLoader from '../../LazyLoader';

interface DatastoreScalingNodeInfoCardProps {
    form: FormInstance;
    datastore: DeploymentsItem | undefined;
    services: any;
    index: number;
    countryCode: string | undefined;
    setCheckedValues: Function;
    checkedValues: string[];
    nodeValue: number | undefined;
    setNewNodeData: Function;
    newNodeData: any[];
    deploymentOptions: DeploymentOptions;
}

export default function DatastoreScalingNodeInfoCard({
    datastore,
    services,
    index,
    countryCode,
    checkedValues,
    setCheckedValues,
    nodeValue,
    form,
    setNewNodeData,
    newNodeData,
    deploymentOptions,
}: DatastoreScalingNodeInfoCardProps) {
    const isScaleDown = nodeValue! / 20 < services?.dbServices?.length;
    const isPrimary =
        services?.dbServices?.[index]?.getRoleName() === 'primary';
    const [instanceSize, setInstanceSize] = useState<InstanceSize[]>();
    const [errorFields, setErrorFields] = useState([]);
    const { instanceSizes, loading } = useContentInstanceSizes(
        datastore?.getCloudProviderCode()
    );

    const availabilityZones = useMemo(() => {
        if (deploymentOptions) {
            return deploymentOptions?.network?.availability_zones[
                datastore?.cloudProvider?.code!
            ][datastore?.cloudRegion!];
        }
    }, [datastore, deploymentOptions]);

    useEffect(() => {
        if (datastore && instanceSizes) {
            const instance = instanceSizes.filter(
                (i: InstanceSizeProps) => i.region === datastore?.cloudRegion
            )[0]?.production;
            setInstanceSize(instance);
        }
    }, [datastore, instanceSizes]);

    const getInstanceTypeName = (type: string) => {
        if (instanceSize) {
            const instanceType = instanceSize?.find(
                (i: InstanceSize) => i.type === type
            )?.name;
            return instanceType;
        }
    };

    const instance_Size = useMemo(() => {
        const getInstanceSize = (type: string) => {
            if (instanceSize) {
                const instanceType = instanceSize?.find(
                    (i: InstanceSize) => i.type === type
                );
                if (instanceType) {
                    const { cpu, ram } = instanceType;
                    return { cpu, ram };
                }
            }
        };

        return getInstanceSize(services?.dbServices?.[index]?.instanceType);
    }, [instanceSize, services, index]);

    const newNodeRoleName = () => {
        return datastore?.getClusterType() === 'Galera'
            ? 'primary (new)'
            : 'replica (new)';
    };

    const roleName = (
        <span
            className={
                services?.dbServices?.[index]?.getRoleName()
                    ? styles.DatastoreScalingNodeInfoCardRole
                    : styles.DatastoreScalingNodeInfoCardRoleSecondary
            }
        >
            {services?.dbServices?.[index]?.getRoleName() || newNodeRoleName()}
        </span>
    );

    const onChange = (e: any) => {
        const az = form.getFieldValue(`availability_zone-${index}`);
        const ins = form.getFieldValue(`instance_size-${index}`);

        if (az && ins) {
            const newData = {
                availability_zone: az,
                instance_size: ins,
            };
            setNewNodeData((prevNodeData: any) => [...prevNodeData, newData]);
        }
    };

    const fieldsSetup = {
        step1: [
            {
                name: [`availability_zone-${index}`],
                required: true,
                label: 'Availability Zone',
                placeholder: 'Availability Zone',
                type: 'select',
                onChange: onChange,
                options: availabilityZones
                    ? availabilityZones?.map((r: any) => {
                          return {
                              label: r.name,
                              value: r.code,
                          };
                      })
                    : [],
            },
            {
                name: [`instance_size-${index}`],
                required: true,
                label: 'Instance Size',
                placeholder: 'Instance Size',
                type: 'select',
                onChange: onChange,
                options: instanceSize
                    ? instanceSize?.map((i) => {
                          return {
                              label: `${i.name}: ${i.cpu} Cores, ${i.ram} GB`,
                              value: i.code,
                          };
                      })
                    : [],
            },
        ],
    };

    const onCheck = (event: Boolean, value: string) => {
        if (event) {
            setCheckedValues((prevIds: any) => [...prevIds, value]);
        } else {
            setCheckedValues((prevIds: any[]) =>
                prevIds.filter((id: string) => id !== value)
            );
        }
    };
    const remainingNodesCount = Math.abs(
        nodeValue! / 20 - services?.dbServices?.length
    );
    return (
        <Col span={12} className={styles.DatastoreScalingNodeInfoCard}>
            {roleName}
            <div className={styles.DatastoreScalingNodeInfoCardCol}>
                <main>
                    <Row>
                        {isScaleDown ? (
                            <>
                                <Col span={22}>
                                    <Checkbox
                                        onChange={(e) =>
                                            onCheck(
                                                e.target.checked,
                                                services?.dbServices?.[index]
                                                    ?.hostUuid
                                            )
                                        }
                                        disabled={
                                            (datastore?.getClusterType() !==
                                                'Galera' &&
                                                isPrimary) ||
                                            (checkedValues.length ===
                                                remainingNodesCount &&
                                                !checkedValues.includes(
                                                    services?.dbServices?.[
                                                        index
                                                    ]?.hostUuid
                                                ))
                                        }
                                        className=""
                                    >
                                        <div
                                            className={
                                                styles.DatastoreScalingNodeIcon
                                            }
                                        >
                                            <CcxIcon1NodeDeployment
                                                height={20}
                                                width={20}
                                            />
                                            <TypographyText strong={true}>
                                                {getHostnameOrIP(
                                                    services?.dbServices?.[
                                                        index
                                                    ]?.getFQDN()
                                                ) || 'New node'}
                                            </TypographyText>
                                        </div>
                                        {checkedValues?.includes(
                                            services?.dbServices?.[index]
                                                ?.hostUuid
                                        ) && (
                                            <TypographyText strong={true}>
                                                <span
                                                    className={
                                                        styles.DatastoreScalingNodeRemove
                                                    }
                                                >
                                                    (will be removed)
                                                </span>
                                                <InfoIcon
                                                    info={
                                                        <span>
                                                            All data on this
                                                            node will be lost.
                                                            This action is
                                                            irreversible.
                                                        </span>
                                                    }
                                                />
                                            </TypographyText>
                                        )}
                                    </Checkbox>
                                </Col>
                                <Col span={2}>
                                    {isPrimary && (
                                        <InfoIcon
                                            info={
                                                <span>
                                                    Primary node cannot be
                                                    removed.
                                                </span>
                                            }
                                        />
                                    )}
                                </Col>
                            </>
                        ) : loading ? (
                            <LazyLoader type="row" rows={1} />
                        ) : (
                            <Col span={24}>
                                <div
                                    className={styles.DatastoreScalingNodeIcon}
                                >
                                    <CcxIcon1NodeDeployment
                                        height={20}
                                        width={20}
                                    />
                                    <TypographyText strong={true}>
                                        {getHostnameOrIP(
                                            services?.dbServices?.[
                                                index
                                            ]?.getFQDN()
                                        ) || 'New node'}
                                    </TypographyText>
                                </div>

                                {services?.dbServices?.[index] && (
                                    <div
                                        className={
                                            styles.DatastoreScalingInstance
                                        }
                                    >
                                        <>
                                            <TypographyText muted={true}>
                                                Instance:{' '}
                                                {getInstanceTypeName(
                                                    services?.dbServices?.[
                                                        index
                                                    ]?.instanceType
                                                )}
                                            </TypographyText>
                                            <br />
                                            <Space
                                                className={
                                                    styles.DatastoreScalingSpace
                                                }
                                            >
                                                <Space size={4}>
                                                    <Tooltip title="CPU">
                                                        <CcxIconCpu
                                                            className={
                                                                styles.DatastoreScalingSpecIcon
                                                            }
                                                        />
                                                    </Tooltip>
                                                    {instance_Size?.cpu}
                                                </Space>
                                                <Space size={4}>
                                                    <Tooltip title="RAM">
                                                        <CcxIconRam
                                                            className={
                                                                styles.DatastoreScalingSpecIcon
                                                            }
                                                        />
                                                    </Tooltip>
                                                    {`${instance_Size?.ram} GB`}
                                                </Space>
                                                {datastore?.volumeSize ? (
                                                    <Space size={4}>
                                                        <Tooltip title="Disk">
                                                            <CcxIconDisk
                                                                className={
                                                                    styles.DatastoreScalingSpecIcon
                                                                }
                                                            />
                                                        </Tooltip>
                                                        {`${datastore?.volumeSize} GB`}
                                                    </Space>
                                                ) : null}
                                                <Space size={4}>
                                                    <Tooltip title="Region">
                                                        {countryCode && (
                                                            <AppFlagIcon
                                                                code={
                                                                    countryCode
                                                                }
                                                            />
                                                        )}
                                                    </Tooltip>
                                                    {services?.dbServices?.[
                                                        index
                                                    ]?.getAvailabilityZone()}
                                                </Space>
                                            </Space>
                                        </>
                                    </div>
                                )}
                                {!services?.dbServices?.[index] && (
                                    <div>
                                        <Form
                                            // onValuesChange={validateFormStep}
                                            layout="vertical"
                                            form={form}
                                            scrollToFirstError={true}
                                        >
                                            <AppForm
                                                fields={fieldsSetup.step1}
                                                errorFields={errorFields}
                                                // useGrid={true}
                                            />
                                        </Form>
                                    </div>
                                )}
                            </Col>
                        )}
                    </Row>
                </main>
            </div>
        </Col>
    );
}
